import { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Canonical Modal for in-app dialogs. Replaces every `window.alert` /
 * `window.confirm` / `window.prompt` site in the codebase.
 *
 * Built on Radix Dialog (focus trap, esc-to-close, scroll-lock, ARIA
 * roles all handled by the primitive). Styled with the Knowlez paper +
 * indigo + hairline tokens so it matches the rest of the product.
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = 'md',
  hideClose,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  hideClose?: boolean;
}) {
  const widthClass = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-md';
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="fixed inset-0 z-50 bg-ink-900/30 backdrop-blur-sm"
              />
            </Dialog.Overlay>
            {/*
              Centering wrapper lives on the Dialog.Content host so the
              motion.div inside is free to own the `transform` style.
              Tailwind's -translate-x-1/2 / -translate-y-1/2 utilities
              get clobbered by framer-motion's animated transform — using
              a grid container avoids that conflict cleanly.
            */}
            <Dialog.Content asChild>
              <div className="fixed inset-0 z-50 grid place-items-center p-4 pointer-events-none">
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.18, ease: [0.16, 0.84, 0.44, 1] }}
                  className={`relative w-full ${widthClass} card-paper p-6 focus:outline-none pointer-events-auto`}
                >
                  {title && (
                    <Dialog.Title className="display text-[18px] font-semibold text-ink-900 tracking-tight">
                      {title}
                    </Dialog.Title>
                  )}
                  {description ? (
                    <Dialog.Description className="mt-1.5 text-[13.5px] text-ink-600 leading-relaxed">
                      {description}
                    </Dialog.Description>
                  ) : (
                    // Always provide an accessible description so Radix doesn't
                    // warn; fall back to the title when no body text is given.
                    <Dialog.Description className="sr-only">
                      {typeof title === 'string' ? title : 'Dialog'}
                    </Dialog.Description>
                  )}
                  <div className={title || description ? 'mt-5' : ''}>{children}</div>

                  {!hideClose && (
                    <Dialog.Close asChild>
                      <button
                        aria-label="Close"
                        className="absolute top-3 right-3 inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-400 hover:text-ink-900 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
                      >
                        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" d="M4 4l8 8M12 4l-8 8" />
                        </svg>
                      </button>
                    </Dialog.Close>
                  )}
                </motion.div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}

/**
 * Two-button confirmation dialog. Use for destructive actions
 * (delete, revoke, disconnect). For non-destructive prompts use Modal
 * directly with form content.
 *
 * Returns nothing; the caller passes onConfirm which fires when the
 * user clicks the danger button. The dialog handles its own dismissal.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  destructive = true,
  pending,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  destructive?: boolean;
  pending?: boolean;
}) {
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} description={description} size="sm">
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          disabled={pending}
          className="btn-ghost px-3.5 py-2 text-sm"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={() => void onConfirm()}
          disabled={pending}
          className={
            destructive
              ? 'inline-flex items-center justify-center px-3.5 py-2 rounded-lg text-sm font-semibold transition bg-warn-500 text-white hover:bg-warn-600 shadow-press disabled:opacity-60'
              : 'btn-press px-3.5 py-2 text-sm'
          }
        >
          {pending ? '…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Tiny inline alert — used in place of a `window.alert()` when the
 * message is informational and a full modal would be overkill. Renders
 * as a hairline-bordered banner inside whatever card it's placed in.
 */
export function InlineAlert({
  tone = 'error',
  children,
  onDismiss,
}: {
  tone?: 'error' | 'info' | 'success';
  children: ReactNode;
  onDismiss?: () => void;
}) {
  const tones: Record<typeof tone, string> = {
    error: 'border-warn-500/30 bg-warn-500/[0.06] text-warn-600',
    info: 'border-accent-200 bg-accent-50/60 text-accent-700',
    success: 'border-emerald-200 bg-emerald-50/60 text-emerald-700',
  };
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-lg border px-3 py-2 text-[13px] leading-relaxed flex items-start gap-2 ${tones[tone]}`}
    >
      <span className="flex-1">{children}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="opacity-60 hover:opacity-100"
        >
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      )}
    </div>
  );
}
